// ============================================
// narb-auth/src/accessibility/detector.js
// ============================================
// Detects whether a user likely needs accessibility-first mode at
// app load, BEFORE the first user-facing render. Returns one of:
//   { mode: 'on', reason: '<why>' }
//   { mode: 'off' }
//
// Three signals, in priority order:
//   1. URL override (?a11y=1) — explicit user/sharer intent, always wins
//   2. Browser screen-reader hints — best-effort DOM detection
//   3. Saved preference (requires auth token + apiBase to fetch)
//
// Detection is best-effort. The toggle button (always present, always
// reachable) is the reliable fallback when detection misses.
// ============================================

/**
 * Run all three detection signals and return the strongest match.
 * Safe to call before render — does not block on auth.
 */
export async function detectAccessibilityNeeds({ token, apiBase } = {}) {
  // Signal 1 — URL override (highest priority)
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('a11y') === '1' || params.get('accessibility') === '1') {
        return { mode: 'on', reason: 'url_override' };
      }
    } catch (err) {
      // URL parsing failed — non-fatal, fall through
    }
  }

  // Signal 2 — Screen reader DOM detection
  if (detectScreenReader()) {
    return { mode: 'on', reason: 'screen_reader_detected' };
  }

  // Signal 3 — Saved preference (requires auth)
  if (token && apiBase) {
    try {
      const response = await fetch(`${apiBase}/api/preferences/accessibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const prefs = data?.data;
        if (prefs && (
          prefs.blindOrLowVision ||
          prefs.screenReaderActive ||
          prefs.onboardingComplete && (
            prefs.ttsEnabled || prefs.dyslexicFont || prefs.highContrast || prefs.largerTapTargets
          )
        )) {
          return { mode: 'on', reason: 'saved_preference', prefs };
        }
      }
    } catch (err) {
      // Network/auth failure — non-fatal, default to mode: off
      console.warn('[accessibility-detector] Could not check saved preference:', err.message);
    }
  }

  return { mode: 'off' };
}

// ============================================
// SCREEN READER DETECTION
// ============================================
// Best-effort detection. Real screen readers don't expose themselves
// to the page directly (privacy), so we use indirect signals:
//
//   1. User Agent string — some screen readers leave traces
//   2. CSS-based detection — some screen reader behaviour creates
//      detectable focus patterns or accessibility tree queries
//
// This will miss many users. That's fine — the universal toggle is
// the reliable fallback. Detection is the "nice surprise on first
// visit" backstop, not the primary mechanism.
// ============================================
function detectScreenReader() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // UA-based detection — known screen reader markers
  const ua = navigator.userAgent || '';
  const screenReaderMarkers = [
    'NVDA',
    'JAWS',
    'VoiceOver',  // rare in UA but checked anyway
    'TalkBack',
    'Orca',
  ];
  if (screenReaderMarkers.some((marker) => ua.includes(marker))) {
    return true;
  }

  // Some screen readers/browsers expose a media query for prefers-reduced-motion
  // alongside accessibility tools. We don't treat reduce-motion alone as a
  // strong signal (many users enable it for other reasons), but combined
  // with other hints it strengthens the case.
  // Currently NOT using this on its own — left here as a marker that this
  // is the place to add additional signals as we learn more.

  return false;
}
